<template>
    <div class="main-container formular-230 has-shadow" :class="{ 'on-mobile-right': screenWidth <= 1023 }">
        <ValidationObserver ref="observer" v-slot="{}">
            <div class="has-text-left">
                <b-loading :is-full-page="false" v-model="isSubmitPending" :can-cancel="false"></b-loading>
                <div
                    class="title has-text-weight-semibold is-size-4 has-text-weight-bold is-flex is-align-content-center is-align-items-center"
                >
                    <div>
                        <b-icon class="mr-3 has-text-grey-lighter" icon="account-details-outline" size="is-large">
                        </b-icon>
                    </div>
                    <div>Date contribuabil</div>
                </div>
                <div class="columns">
                    <div class="column">
                        <ValidationProvider
                            rules="required|alpha_spaces_minus|min:2|max:45"
                            name="nume"
                            v-slot="{ errors, valid }"
                        >
                            <b-field :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                <b-input
                                    size="is-medium"
                                    placeholder="Nume"
                                    v-model.trim="form.nume"
                                    minlength="1"
                                ></b-input>
                            </b-field>
                        </ValidationProvider>
                    </div>
                    <div class="column">
                        <ValidationProvider
                            rules="required|alpha_spaces_minus|min:2|max:45"
                            name="prenume"
                            v-slot="{ errors, valid }"
                        >
                            <b-field :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                <b-input
                                    size="is-medium"
                                    placeholder="Prenume"
                                    v-model.trim="form.prenume"
                                    minlength="1"
                                ></b-input>
                            </b-field>
                        </ValidationProvider>
                    </div>
                </div>
                <div class="columns">
                    <div class="column">
                        <ValidationProvider rules="max:1|alpha" name="initiala" v-slot="{ errors, valid, changed }">
                            <b-field
                                :type="{ 'is-danger': errors[0], 'is-success': valid && changed }"
                                :message="errors"
                            >
                                <b-input
                                    size="is-medium"
                                    placeholder="Initiala tata"
                                    v-model.trim="form.initiala"
                                    minlength="1"
                                ></b-input>
                            </b-field>
                        </ValidationProvider>
                    </div>
                    <div class="column">
                        <ValidationProvider
                            rules="required|cnplength:13|cnpinteger|cnpformat|cnpvalability"
                            name="CNP"
                            vid="cnp"
                            v-slot="{ errors, valid }"
                        >
                            <b-field :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                <b-input
                                    :loading="!isCNPReady"
                                    :disabled="!isCNPReady"
                                    size="is-medium"
                                    class="is-fulllength"
                                    placeholder="CNP"
                                    v-model.trim="form.cnp"
                                    minlength="1"
                                ></b-input>
                            </b-field>
                        </ValidationProvider>
                        <p class="has-text-info is-size-7" v-if="oneYearPeriod">
                            Puteti redirectiona doar pentru anul 2025
                        </p>
                    </div>
                </div>
                <div class="columns">
                    <div class="column">
                        <ValidationProvider rules="email" name="email" v-slot="{ errors, valid, changed }">
                            <b-field
                                :type="{
                                    'is-danger': errors[0],
                                    'is-success': valid && changed,
                                }"
                                :message="errors"
                            >
                                <b-input
                                    size="is-medium"
                                    placeholder="Email"
                                    type="email"
                                    v-model.trim="form.email"
                                    minlength="1"
                                ></b-input>
                            </b-field>
                        </ValidationProvider>
                    </div>
                    <div class="column">
                        <ValidationProvider
                            rules="required|phoneformat|phonedigits:10|phonevalability"
                            name="telefon"
                            vid="telefon"
                            v-slot="{ errors, valid }"
                        >
                            <b-field :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                <b-input
                                    :loading="!isTelefonReady"
                                    :disabled="!isTelefonReady"
                                    size="is-medium"
                                    placeholder="Telefon"
                                    v-model.trim="form.telefon"
                                    minlength="1"
                                ></b-input>
                            </b-field>
                        </ValidationProvider>
                    </div>
                </div>

                <div
                    class="subtitle has-text-weight-semibold is-size-4 has-text-weight-bold is-flex is-align-content-center is-align-items-center"
                >
                    <div>
                        <b-icon class="mr-3 has-text-grey-lighter" icon="map-marker-radius-outline" size="is-large">
                        </b-icon>
                    </div>
                    <div>Adresa</div>
                </div>
                <div class="columns">
                    <div class="column">
                        <ValidationProvider vid="county" rules="required" name="judet" v-slot="{ errors, valid }">
                            <b-field :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                <b-select
                                    :loading="isCountyLoading"
                                    @input="onCountySelect"
                                    size="is-medium"
                                    placeholder="Judet"
                                    v-model="form.judet"
                                    expanded
                                >
                                    <option class="placeholder-option" value="" selected>Alege judetul</option>
                                    <option v-for="judet in judete" :key="judet.id" :value="judet.name">
                                        {{ judet.name }}
                                    </option>
                                </b-select>
                            </b-field>
                        </ValidationProvider>
                    </div>
                    <div class="column">
                        <ValidationProvider vid="city" rules="required" name="localitate" v-slot="{ errors, valid }">
                            <b-field :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                <b-select
                                    :loading="isCityLoading"
                                    placeholder="Localitate"
                                    size="is-medium"
                                    v-model="form.localitate"
                                    :disabled="form.judet == null || form.judet === ''"
                                    expanded
                                >
                                    <option class="placeholder-option" value="" selected>
                                        Alege {{ form.judet === 'Bucuresti' ? 'sectorul' : 'localitatea' }}
                                    </option>
                                    <option
                                        v-for="localitate in localitati"
                                        :value="localitate.name"
                                        :key="localitate.id"
                                    >
                                        {{ localitate.name }}
                                    </option>
                                </b-select>
                            </b-field>
                        </ValidationProvider>
                    </div>
                </div>
                <div class="columns">
                    <div class="column">
                        <ValidationProvider rules="required|max:45" name="strada" v-slot="{ errors, valid }">
                            <b-field :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                <b-input
                                    size="is-medium"
                                    placeholder="Strada"
                                    v-model.trim="form.strada"
                                    minlength="1"
                                ></b-input>
                            </b-field>
                        </ValidationProvider>
                    </div>
                </div>
                <div class="columns">
                    <div class="column">
                        <ValidationProvider rules="max:10" name="numar" v-slot="{ errors, valid, changed }">
                            <b-field
                                :type="{
                                    'is-danger': errors[0],
                                    'is-success': valid && changed,
                                }"
                                :message="errors"
                            >
                                <b-input
                                    size="is-medium"
                                    placeholder="Numar"
                                    v-model.trim="form.numar"
                                    minlength="1"
                                ></b-input>
                            </b-field>
                        </ValidationProvider>
                    </div>
                    <div class="column">
                        <ValidationProvider rules="max:8" name="bloc" v-slot="{ errors, valid, changed }">
                            <b-field
                                :type="{
                                    'is-danger': errors[0],
                                    'is-success': valid && changed,
                                }"
                                :message="errors"
                            >
                                <b-input
                                    size="is-medium"
                                    placeholder="Bloc"
                                    v-model.trim="form.bloc"
                                    minlength="1"
                                ></b-input>
                            </b-field>
                        </ValidationProvider>
                    </div>
                    <div class="column">
                        <ValidationProvider rules="max:8" name="scara" v-slot="{ errors, valid, changed }">
                            <b-field
                                :type="{
                                    'is-danger': errors[0],
                                    'is-success': valid && changed,
                                }"
                                :message="errors"
                            >
                                <b-input
                                    size="is-medium"
                                    placeholder="Scara"
                                    v-model.trim="form.scara"
                                    minlength="1"
                                ></b-input>
                            </b-field>
                        </ValidationProvider>
                    </div>
                    <div class="column">
                        <ValidationProvider rules="max:5" name="etaj" v-slot="{ errors, valid, changed }">
                            <b-field
                                :type="{
                                    'is-danger': errors[0],
                                    'is-success': valid && changed,
                                }"
                                :message="errors"
                            >
                                <b-input
                                    size="is-medium"
                                    placeholder="Etaj"
                                    v-model.trim="form.etaj"
                                    minlength="1"
                                ></b-input>
                            </b-field>
                        </ValidationProvider>
                    </div>
                    <div class="column">
                        <ValidationProvider rules="max:5" name="apartament" v-slot="{ errors, valid, changed }">
                            <b-field
                                :type="{
                                    'is-danger': errors[0],
                                    'is-success': valid && changed,
                                }"
                                :message="errors"
                            >
                                <b-input
                                    size="is-medium"
                                    placeholder="Apartament"
                                    v-model.trim="form.apartament"
                                    minlength="1"
                                ></b-input>
                            </b-field>
                        </ValidationProvider>
                    </div>
                </div>
                <hr />
                <div class="columns is-desktop is-align-content-center is-align-items-center">
                    <div class="column">
                        <div
                            class="subtitle has-text-weight-semibold is-size-4 has-text-weight-bold is-flex is-align-content-center is-align-items-center"
                        >
                            <div>
                                <b-icon
                                    class="mr-3 has-text-grey-lighter"
                                    icon="calendar-month-outline"
                                    size="is-large"
                                >
                                </b-icon>
                            </div>
                            <div>Perioada</div>
                        </div>
                    </div>
                    <div class="column font-14">Alegeti perioada de distribuire a contributiei</div>
                    <div class="column">
                        <ValidationProvider rules="required" name="localitate" v-slot="{ errors, valid }">
                            <b-field :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                <b-radio-button
                                    class="period-form"
                                    type="is-success"
                                    size="is-medium"
                                    v-model="form.perioada"
                                    name="perioada"
                                    native-value="1"
                                >
                                    1 an
                                </b-radio-button>
                                <b-radio-button
                                    :disabled="oneYearPeriod"
                                    class="period-form"
                                    type="is-success"
                                    size="is-medium"
                                    v-model="form.perioada"
                                    name="perioada"
                                    native-value="2"
                                    checked
                                >
                                    2 ani
                                </b-radio-button>
                            </b-field>
                        </ValidationProvider>
                    </div>
                </div>
                <hr />
                <div
                    class="mb-2 subtitle has-text-weight-semibold is-size-4 has-text-weight-bold is-flex is-align-content-center is-align-items-center"
                >
                    <div>
                        <b-icon class="mr-3 has-text-grey-lighter" icon="file-image" size="is-large"> </b-icon>
                    </div>
                    <div>Poza formular</div>
                </div>
                <div class="columns mb-1 is-align-content-center is-align-items-center">
                    <div
                        class="column is-two-thirds font-14"
                        :class="{
                            'is-one-thirds': screenWidth >= 1228,
                            'is-three-fifths': screenWidth < 1228,
                        }"
                    >
                        Incarcati poza formularului completat de mana
                        <br />
                        Doar fisiere
                        <strong>PDF, JPEG, JPG, GIF, PNG sunt acceptate (Max. 8 MB)</strong>
                    </div>
                    <div
                        class="column"
                        :class="{
                            'has-text-right': screenWidth > 1228,
                            'is-one-thirds': screenWidth >= 1228,
                            'is-two-fifths': screenWidth < 1228,
                        }"
                    ></div>
                </div>
                <div class="columns">
                    <div class="column">
                        <p class="mt-2 has-text-danger" v-if="false">Semnatura este obligatorie</p>
                        <ValidationProvider rules="required|manualFormSize:8000" name="file" v-slot="{ errors, valid }">
                            <b-field :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                <b-upload required accept=".pdf,.jpg,.jpeg,.gif,.png" expanded v-model="file" drag-drop>
                                    <section class="section">
                                        <div class="content has-text-centered">
                                            <p>
                                                <b-icon icon="upload" size="is-large"> </b-icon>
                                            </p>
                                            <p>Trage aici poza sau click pentru a alege</p>
                                        </div>
                                    </section>
                                </b-upload>
                            </b-field>
                            <div v-if="file">
                                <span class="tag is-size-6 expanded is-danger is-file-tag" @click="deleteDropFile()">
                                    {{ file.name }}
                                    <b-icon icon="close-circle-outline"></b-icon>
                                </span>
                                <div class="preview" v-if="fileIsImage">
                                    <b-image :src="filePreview" responsive></b-image>
                                </div>
                                <div class="pdf-preview" v-else-if="fileIsPDF">
                                    <pdf :src="filePreview"></pdf>
                                </div>
                            </div>
                        </ValidationProvider>
                    </div>
                </div>
                <b-button
                    size="is-large"
                    type="is-block  has-text-weight-bold is-success is-fullwidth"
                    @click="submit"
                    :loading="isSubmitPending"
                    :disabled="!isTelefonReady || !isCNPReady"
                    >SALVEAZA FORMULARUL</b-button
                >
            </div>
        </ValidationObserver>
    </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import * as toast from '@/utils/toast';
import { mapGetters } from 'vuex';
import pdf from 'vue-pdf';

const blankForm = {
    nume: '',
    initiala: '',
    prenume: '',
    strada: '',
    numar: '',
    bloc: '',
    scara: '',
    etaj: '',
    apartament: '',
    cnp: '',
    email: '',
    telefon: '',
    judet: '',
    localitate: '',
    perioada: '2',
};

export default {
    components: {
        ValidationObserver,
        ValidationProvider,
        pdf,
    },
    data: () => ({
        form: { ...blankForm },
        file: null,
        screenWidth: 1920,
        isSubmitPending: false,
        isCityLoading: false,
        isCountyLoading: false,
        CNPRegistrationMessage: '',
        cnpDebounce: null,
        filePreview: null,
    }),
    computed: {
        ...mapGetters({
            isCNPReady: 'formular/isCNPReady',
            isTelefonReady: 'formular/isTelefonReady',
            judete: 'app/counties',
            localitati: 'app/cities',
            oneYearPeriod: 'formular/oneYearPeriod',
        }),
        fileIsImage() {
            return this.file && this.file['type'].split('/')[0] === 'image';
        },
        fileIsPDF() {
            return this.file && this.file.type == 'application/pdf';
        },
    },
    methods: {
        deleteDropFile(index) {
            this.file = null;
        },
        async submit() {
            const observer = this.$refs.observer;
            const success = await observer.validate();
            // console.log(await fileToBase64(this.form.file));
            if (!success) {
                for (const key of Object.keys(observer.fields).sort()) {
                    if (observer.fields[key].invalid) {
                        observer.refs[key].$el.scrollIntoView();
                        window.scrollBy(0, -200);
                        return;
                    }
                }
            }

            let filePath = '';
            try {
                filePath = await this.$store.dispatch('family/SubmitFormFile', { file: this.file, cnp: 122132323 });
                if (filePath == null || filePath.length != 1 || filePath[0].filePath == null) {
                    throw new Error();
                }
                filePath = filePath[0].filePath;
            } catch (e) {
                this.$reportToSentry(e, {
                    extra: {
                        fn: 'familyFileUpload',
                        params: {
                            event: 'familyFileUpload',
                            error: e,
                            file: this.file,
                        },
                    },
                });
                return toast.error('Eroare la incarcarea documentului/pozei', 8000);
            }

            const params = Object.assign({ filePath }, this.form);

            this.isSubmitPending = true;
            try {
                await this.$store.dispatch('family/SaveManualForm', params);
                this.form = { ...blankForm };
                this.$nextTick(() => this.$refs.observer.reset());
                this.file = null;
                this.$store.commit('formular/SET_FORMULAR_ONE_YEAR_STATUS', false);
                toast.success('Formularul a fost salvat. Imediat ce va fi aprobat va aparea in lista');
            } catch (err) {
                this.$reportToSentry(err, {
                    extra: {
                        fn: 'SaveManualForm',
                        params: {
                            event: 'SaveManualForm',
                            params,
                            err,
                        },
                    },
                });
                toast.error(`Eroare la salvarea formularului ${err.message}`);
            } finally {
                setTimeout(() => {
                    this.isSubmitPending = false;
                }, 1500);
            }

            return true;
        },
        windowSizeChanged(e) {
            this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        },
        async onCountySelect(countyName) {
            const countyObj = this.judete.find((judet) => judet.name === countyName);
            if (countyObj == null) {
                return toast.error('Judet invalid');
            }
            this.isCityLoading = true;
            try {
                await this.$store.dispatch('app/GetCitiesByCountyId', countyObj.id);
                this.$refs.observer.refs.localitate.setFlags({ changed: true, touched: true, untouched: false });
            } catch (err) {
                console.log(err);
                this.$reportToSentry(err, {
                    extra: {
                        fn: 'GetCitiesByCountyId',
                        params: {
                            event: 'GetCitiesByCountyId',
                            countyObj,
                            countyName,
                            err,
                        },
                    },
                });
                toast.error('Eroare la preluarea localitatilor');
            } finally {
                this.isCityLoading = false;
            }
        },
    },
    watch: {
        oneYearPeriod(value, oldValue) {
            if (value == true && +this.form.perioada === 2) {
                this.form.perioada = '1';
            } else this.form.perioada = '2';
        },
        'form.judet'(val, oldVal) {
            if (oldVal !== '') {
                this.form.localitate = '';
            }
        },
        file() {
            if (!this.fileIsImage && !this.fileIsPDF) return (this.filePreview = null);
            if (this.fileIsImage) {
                var reader = new FileReader();
                reader.onload = (e) => {
                    this.filePreview = e.target.result;
                };
                reader.readAsDataURL(this.file);
            } else {
                this.filePreview = URL.createObjectURL(this.file);
            }
        },
    },
    async created() {
        this.isSubmitPending = true;
        window.addEventListener('resize', this.windowSizeChanged);
        this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

        this.isCountyLoading = true;
        try {
            await this.$store.dispatch('app/GetCounties');
        } catch (err) {
            this.$reportToSentry(err, {
                extra: {
                    fn: 'FamilyGetCounties',
                    params: {
                        event: 'FamilyGetCounties',

                        err,
                    },
                },
            });
            toast.error('Eroare la preluarea judetelor', 6000, 'is-top');
        } finally {
            this.isCountyLoading = false;
            this.isSubmitPending = false;
        }

        // setTimeout(() => {
        //     this.form = {
        //         nume: 'Tanase',
        //         initiala: '',
        //         prenume: 'Alin',
        //         strada: 'sesase',
        //         numar: '',
        //         bloc: '',
        //         scara: '',
        //         etaj: '',
        //         apartament: '',
        //         cnp: '6200708047611',
        //         email: 'alintanase92@gmail.com',
        //         telefon: '0762985485',
        //         judet: 'Bucuresti',
        //         // localitate: 'Sector 3',
        //         perioada: '2',
        //     };
        // }, 1000);
    },
    beforeDestroy() {
        document.documentElement.className = '';
        window.removeEventListener('resize', this.windowSizeChanged);
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    padding: 0;
    position: relative;
}

.font-14 {
    font-size: 0.9rem;
    font-weight: 500;
}

.period-form {
    &:not(:last-child) {
        margin-right: 20px !important;
    }

    .radio {
        border-radius: 10px !important;
    }
}

.is-size-4 {
    font-size: 1.5rem !important;
}

hr {
    background-color: #dbdbdb;
}

.info {
    display: table;
    margin: 0 auto;
    margin-top: 50px;
    padding: 40px;
    opacity: 0.9;

    .info-text {
        font-size: 0.9rem;
        padding-top: 5px;
    }
}

.on-mobile-left {
    border-radius: 6px 6px 0px 0px;
}
.on-mobile-right .box {
    border-radius: 0px 0px 6px 6px;
}

.form-title {
    font-size: 1.8rem;
}

.text-with-border {
    border: 1px solid;
    background-color: white;
    border-color: #dbdbdb;
    border-radius: 4px;
    color: #363636;
    height: 3.5em;
    font-size: 0.9rem;
    line-height: 1.5;
    padding-bottom: calc(0.5em - 1px);
    padding-left: calc(0.75em - 1px);
    padding-right: calc(0.75em - 1px);
    padding-top: calc(0.5em - 1px);
}

::v-deep .on-mobile input:disabled {
    -webkit-appearance: none;
    color: #7a7a7a !important;
    background-color: #f5f5f5bf;
    -webkit-text-fill-color: #7a7a7a !important;
    -webkit-opacity: 1 !important;
    opacity: 1 !important;
}

.overlay-invalid {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    align-items: center;
    display: flex;
    justify-content: center;
    overflow: hidden;
    z-index: 999;

    .ov-background {
        bottom: 0;
        left: 0;
        position: absolute;
        right: 0;
        top: 0;
        background: #7f7f7f;
        background: rgb(255 255 255 / 78%);
    }

    .ov-text {
        padding: 50px;
        background-color: #296c9e;
        z-index: 120;
        color: #fff;
        border-radius: 6px;
        position: fixed;
        top: 20em;
        box-shadow: 6px 6px 5px rgb(0 0 0 / 33%);
    }
}

::v-deep .snackbar em {
    font-size: 0.95rem;
}

.is-file-tag {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 5px 5px 0px 0px;
    cursor: pointer;
}
</style>
